import type { AudioState, ResearchTopic } from '../types'
import {
  createIntelligencePodcast,
  getIntelligencePodcastInfo,
  type PodcastInfo,
} from './api'

const DEFAULT_POLL_INTERVAL_MS = 4000
const DEFAULT_MAX_POLL_ATTEMPTS = 18

const isPendingStatus = (status?: PodcastInfo['status']) =>
  status === 'queued' || status === 'processing'

const normalizeAudioStatus = (
  status?: PodcastInfo['status']
): AudioState['status'] => {
  if (status === 'queued' || status === 'processing' || status === 'ready' || status === 'failed') {
    return status
  }
  return 'idle'
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export type PodcastActionState = {
  visible: boolean
  label: string
  disabled: boolean
  status: 'hidden' | 'missing' | 'queued' | 'processing' | 'ready' | 'failed' | 'playing'
  showSpinner: boolean
}

export function getCompactPodcastActionLabel(label: string): string {
  switch (label) {
    case 'Retry Audio':
      return 'Retry'
    case 'Prepare Audio':
      return 'Prepare'
    case 'Play Podcast':
      return 'Play'
    default:
      return label
  }
}

function resolveReportedPodcastStatus(input: {
  status?: PodcastInfo['status']
  audioReady?: boolean
  errorMessage?: string
}): PodcastInfo['status'] {
  if (input.audioReady) {
    return 'ready'
  }
  if (input.status === 'ready') {
    return input.errorMessage ? 'failed' : 'missing'
  }
  return input.status || 'missing'
}

function normalizeResolvedPodcastInfo(podcast: PodcastInfo): PodcastInfo {
  return {
    ...podcast,
    status: resolveReportedPodcastStatus({
      status: podcast.status,
      audioReady: Boolean(podcast.audio_url),
      errorMessage: podcast.error_message,
    }),
  }
}

export function getPodcastActionState(
  topic: Pick<ResearchTopic, 'id' | 'is_intelligence_report' | 'intelligence_report_id' | 'podcast_status' | 'podcast_audio_ready' | 'podcast_error_message'>,
  audioState?: AudioState
): PodcastActionState {
  if (!topic.is_intelligence_report || !topic.intelligence_report_id) {
    return {
      visible: false,
      label: '',
      disabled: true,
      status: 'hidden',
      showSpinner: false,
    }
  }

  const activeReportId = audioState?.topic?.intelligence_report_id
  const isCurrentTrack = activeReportId === topic.intelligence_report_id
  if (isCurrentTrack && audioState?.isPlaying) {
    return {
      visible: true,
      label: 'Pause',
      disabled: false,
      status: 'playing',
      showSpinner: false,
    }
  }

  const status = resolveReportedPodcastStatus({
    status: topic.podcast_status,
    audioReady: topic.podcast_audio_ready,
    errorMessage: topic.podcast_error_message,
  })
  if (status === 'queued' || status === 'processing') {
    return {
      visible: true,
      label: 'Preparing',
      disabled: true,
      status,
      showSpinner: true,
    }
  }

  if (status === 'failed') {
    return {
      visible: true,
      label: 'Retry Audio',
      disabled: false,
      status,
      showSpinner: false,
    }
  }

  if (status === 'ready') {
    return {
      visible: true,
      label: 'Play Podcast',
      disabled: false,
      status,
      showSpinner: false,
    }
  }

  return {
    visible: true,
    label: 'Prepare Audio',
    disabled: false,
    status: 'missing',
    showSpinner: false,
  }
}

export function buildPodcastAudioState(topic: ResearchTopic, podcast: PodcastInfo): AudioState {
  const enrichedTopic: ResearchTopic = {
    ...topic,
    audio_url: podcast.audio_url,
    artwork_url: podcast.artwork_url || topic.artwork_url || topic.image_url,
    duration_seconds: podcast.duration_seconds ?? topic.duration_seconds,
  }

  return {
    isPlaying: true,
    currentTime: 0,
    duration: podcast.duration_seconds || 0,
    topic: enrichedTopic,
    status: podcast.audio_url ? 'ready' : normalizeAudioStatus(podcast.status),
  }
}

export function dispatchPodcastPlayback(topic: ResearchTopic, podcast: PodcastInfo) {
  if (!podcast.audio_url || typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent('insightatlas-play-podcast', {
      detail: {
        topic: {
          ...topic,
          audio_url: podcast.audio_url,
          artwork_url: podcast.artwork_url || topic.artwork_url || topic.image_url,
          duration_seconds: podcast.duration_seconds ?? topic.duration_seconds,
        },
        audio_url: podcast.audio_url,
      },
    })
  )
}

export async function resolveIntelligencePodcastPlayback(
  reportId: string,
  options: {
    pollIntervalMs?: number
    maxPollAttempts?: number
    onPending?: (podcast: PodcastInfo) => void
  } = {}
): Promise<PodcastInfo> {
  const {
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxPollAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
    onPending,
  } = options

  const existing = await getIntelligencePodcastInfo(reportId)
  if (existing.audio_url) {
    return normalizeResolvedPodcastInfo(existing)
  }

  const created = await createIntelligencePodcast(reportId)
  if (created.audio_url) {
    return normalizeResolvedPodcastInfo(created)
  }

  if (!isPendingStatus(created.status) && !isPendingStatus(existing.status)) {
    return normalizeResolvedPodcastInfo(created.exists ? created : existing)
  }

  onPending?.(created.exists ? created : existing)

  for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
    await sleep(pollIntervalMs)
    const next = await getIntelligencePodcastInfo(reportId)
    if (next.audio_url || next.status === 'failed') {
      return normalizeResolvedPodcastInfo(next)
    }
  }

  return normalizeResolvedPodcastInfo(await getIntelligencePodcastInfo(reportId))
}
